import { Amplify, Auth } from 'aws-amplify';
import { isNull } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useBoolean } from 'react-use';
import { createSearchParams, Outlet, useNavigate } from 'react-router-dom';
import { useLDClient } from 'launchdarkly-react-client-sdk';
import * as Sentry from '@sentry/react';
import { CognitoUserContext, CognitoUserCtx } from 'domains/auth/AWSCognitoWrapper/context';
import config from 'config';
import { Loading } from 'components/AppLoader/Loading';
import { CognitoUser } from 'domains/auth/AWSCognitoWrapper/types';
import { LDContext } from 'utils/launchdarkly/launchdarkly';

Amplify.configure({
  aws_project_region: config.get('awsProjectRegion'),
  aws_cognito_region: config.get('awsCognitoRegion'),
  aws_user_pools_id: config.get('awsUserPoolsId'),
  aws_user_pools_web_client_id: config.get('awsUserPoolsWebClientId'),
  oauth: {},
});

/**
 * This component acts as a container for user session, will automatically read if one exists
 */
export const AWSCognitoWrapper = () => {
  const [isLoadingSession, setIsLoadingSession] = useBoolean(true);

  const [user, setUser] = useState<CognitoUser | null>(null);

  const ldClient = useLDClient();
  const navigate = useNavigate();

  const onSuccessfulLogin = useCallback(
    (u: CognitoUser) => {
      Sentry.setTag('isLoggedIn', true);
      Sentry.setUser({
        email: u.attributes.email,
        id: u.username,
      });
      setUser(u);

      ldClient?.identify({
        kind: LDContext.PORTAL_CLIENT,
        key: u.attributes.email,
        email: u.attributes.email,
        anonymous: false,

        application: 'portal',
      });
    },
    [ldClient],
  );

  const onAttemptLogin = useCallback(async (email: string, password: string) => {
    const user = await Auth.signIn(email, password.trim());
    // https://docs.amplify.aws/lib/auth/manageusers/q/platform/js/#complete-new-password
    if (user.challengeName === 'NEW_PASSWORD_REQUIRED') {
      navigate({
        pathname: '/invitation',
        search: `?${createSearchParams({
          email,
          code: password,
        })}`,
      });
    } else {
      onSuccessfulLogin(user as CognitoUser);
    }
  }, []);

  const readSavedSession = useCallback(() => {
    Auth.currentAuthenticatedUser()
      .then(u => {
        onSuccessfulLogin(u);
      })
      .catch(() => {
        setUser(null);
      })
      .finally(() => setIsLoadingSession(false));
  }, []);

  useEffect(() => {
    readSavedSession();
  }, []);

  const logout = useCallback(() => {
    setUser(null);

    return Auth.signOut();
  }, []);

  const onRefreshSession = useCallback(() => {
    setIsLoadingSession(true);
    readSavedSession();
  }, []);

  if (isLoadingSession) {
    return <Loading className="transition-all duration-100 z-10" />;
  }

  const contextValue: CognitoUserContext = isNull(user)
    ? {
        isLoggedIn: false,
        onRefreshSession,
        logIn: onAttemptLogin,
      }
    : {
        isLoggedIn: true,
        signOut: logout,
        user,
        onRefreshSession,
      };

  return (
    <CognitoUserCtx.Provider value={contextValue}>
      <Outlet />
    </CognitoUserCtx.Provider>
  );
};
